import { useCallback, useEffect, useRef, useState } from "react";

const extFor = (mime) => (mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : "webm");

// Voice recording kept in browser memory only; never uploaded.
export function useVoiceRecorder() {
  const [enabled, setEnabled] = useState(false);
  const [take, setTake] = useState(null);
  const [error, setError] = useState("");
  const [recording, setRecording] = useState(false);
  const stream = useRef(null);
  const rec = useRef(null);
  const pending = useRef(false);
  const takeRef = useRef(null);

  const setTakeSafe = (t) => {
    if (takeRef.current) URL.revokeObjectURL(takeRef.current.url);
    takeRef.current = t;
    setTake(t);
  };

  const stopTracks = () => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  };

  const discard = useCallback(() => {
    if (rec.current && rec.current.state !== "inactive") {
      rec.current.onstop = null;
      rec.current.stop();
    }
    rec.current = null;
    setRecording(false);
    setTakeSafe(null);
  }, []);

  const toggle = async () => {
    if (pending.current) return;
    if (enabled) {
      discard();
      stopTracks();
      setEnabled(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("L'enregistrement n'est pas disponible sur ce navigateur.");
      return;
    }
    pending.current = true;
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      setEnabled(true);
      setError("");
    } catch {
      setError("Micro inaccessible : autorise l'accès au micro dans ton navigateur pour t'enregistrer.");
    } finally {
      pending.current = false;
    }
  };

  const start = useCallback(() => {
    if (!stream.current) return;
    if (rec.current?.state === "paused") return rec.current.resume();
    if (rec.current?.state === "recording") return;
    const chunks = [];
    const r = new MediaRecorder(stream.current);
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = () => {
      setRecording(false);
      if (!chunks.length) return;
      const type = r.mimeType || chunks[0].type || "audio/webm";
      const blob = new Blob(chunks, { type });
      setTakeSafe({ url: URL.createObjectURL(blob), ext: extFor(type) });
    };
    r.start();
    rec.current = r;
    setRecording(true);
  }, []);

  const pause = useCallback(() => {
    if (rec.current?.state === "recording") rec.current.pause();
  }, []);

  const stop = useCallback(() => {
    if (rec.current && rec.current.state !== "inactive") rec.current.stop();
    rec.current = null;
  }, []);

  useEffect(() => () => {
    if (rec.current && rec.current.state !== "inactive") {
      rec.current.onstop = null;
      rec.current.stop();
    }
    if (takeRef.current) URL.revokeObjectURL(takeRef.current.url);
    stopTracks();
  }, []);

  return { enabled, toggle, start, pause, stop, discard, url: take?.url || null, ext: take?.ext || "webm", error, recording };
}
